// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import baseTheme from "@insite/mobius/globals/baseTheme";
import { breakpointKeys } from "@insite/mobius/utilities/breakpointMediaQueries";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

export const mediaOptions = {
    min: "min",
    max: "max",
    minMax: "minMax",
} as const;

/**
 * Creates media queries based on the theme's breakpoints.
 * @param theme The theme object. Defaults to the baseTheme.
 * @param rules An array of CSS rules to be inserted in each media query.
 * @param option Flag describing how the styling is implemented within breakpoint ranges.
 */
export default function xBreakpointMediaQueries(
    theme = baseTheme,
    rules: (StyledProp | null)[] = [],
    option: keyof typeof mediaOptions = mediaOptions.minMax,
    isEqual?: boolean,
) {
    const mediaRules = rules.map((rule, index) => {
        const { values } = theme.breakpoints || baseTheme.breakpoints;
        if (!rule) {
            return null;
        }
        if (option === mediaOptions.minMax) {
            switch (index) {
                case 0:
                    return css`
                        @media (max-width: ${values[index + 1] - 1}px) {
                            ${rule}
                        }
                    `;
                case values.length - 1:
                    return css`
                        @media (min-width: ${values[index]}px) {
                            ${rule}
                        }
                    `;
                default:
                    return css`
                        @media (min-width: ${values[index]}px) and (max-width: ${values[index + 1] - 1}px) {
                            ${rule}
                        }
                    `;
            }
        }

        if (option === mediaOptions.min) {
            return css`
                @media (min-width: ${values[index] + (isEqual ? -1 : 0)}px) {
                    ${rule}
                }
            `;
        }

        if (option === mediaOptions.max) {
            return css`
                @media (max-width: ${values[index + 1] - (isEqual ? 1 : 2)}px) {
                    ${rule}
                }
            `;
        }

        return null;
    });
    /* stylelint-disable */
    return css`
        ${mediaRules[0]}${mediaRules[1]}${mediaRules[2]}${mediaRules[3]}${mediaRules[4]}
    `
        .join("\n")
        .trim();
    /* stylelint-enable */
}
